/**
 * Latency panels shared by the cross-model Statistics page and the per-endpoint
 * Analytics tab.
 *
 * The two charts split the life of a request in half at the moment it starts
 * running: everything before is *startup* (queue backlog, plus container boot
 * for the unlucky request that lands on a cold instance), everything after is
 * *execution*. Splitting them matters because they have different cures —
 * startup is a capacity/scaling problem, execution is a provider problem.
 */
import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatDistanceToNow } from 'date-fns';
import type { ApiStatsCallRow, ApiStatsResponse } from '../types';
import { InlineCopyButton } from './apiSnippets';
import { formatSeconds, type LatencyPoint } from './latency';

// One colour per percentile, darkest for the tail — the same ordering the
// legend lists them in.
const COLOR_P50 = '#6366f1';
const COLOR_P75 = '#0ea5e9';
const COLOR_P90 = '#8b5cf6';
const COLOR_QUEUE = '#f59e0b';
const COLOR_BOOT = '#ef4444';

const CHART_TOOLTIP_STYLE = {
  backgroundColor: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  fontSize: '12px',
};

function PanelCard({
  title,
  subtitle,
  icon,
  right,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  right?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-slate-200 shadow-soft bg-white overflow-hidden">
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
              {icon}
              {title}
            </h3>
            {subtitle && <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>}
          </div>
          {right}
        </div>
        {children}
      </div>
      {footer && (
        <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-2">{footer}</div>
      )}
    </div>
  );
}

function LatencyChart({
  points,
  loading,
  hasData,
  showBreakdown = false,
  emptyLabel,
}: {
  points: LatencyPoint[];
  loading: boolean;
  hasData: boolean;
  showBreakdown?: boolean;
  emptyLabel: string;
}) {
  return (
    <div className="h-56 mt-3">
      {loading && !hasData ? (
        <div className="h-full flex items-center justify-center text-sm text-slate-500">
          Loading…
        </div>
      ) : hasData ? (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={points} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: '#64748b' }}
              interval="preserveStartEnd"
              minTickGap={24}
            />
            <YAxis
              tick={{ fontSize: 10, fill: '#64748b' }}
              tickFormatter={(v: number) => (v >= 1 ? String(Math.round(v)) : v.toFixed(2))}
            />
            <Tooltip
              contentStyle={CHART_TOOLTIP_STYLE}
              formatter={(value: number, name: string) => [formatSeconds(value), name]}
            />
            <Legend
              verticalAlign="bottom"
              height={24}
              formatter={(value: string) => <span className="text-slate-600 text-xs">{value}</span>}
            />
            <Line type="monotone" dataKey="p90" name="p90" stroke={COLOR_P90} strokeWidth={1.5} dot={false} />
            <Line type="monotone" dataKey="p75" name="p75" stroke={COLOR_P75} strokeWidth={1.5} dot={false} />
            <Line type="monotone" dataKey="p50" name="p50" stroke={COLOR_P50} strokeWidth={1.5} dot={false} />
            {showBreakdown && (
              <Line
                type="monotone"
                dataKey="queue"
                name="queue"
                stroke={COLOR_QUEUE}
                strokeWidth={1}
                strokeDasharray="4 3"
                dot={false}
              />
            )}
            {showBreakdown && (
              <Line
                type="monotone"
                dataKey="boot"
                name="boot"
                stroke={COLOR_BOOT}
                strokeWidth={1}
                strokeDasharray="4 3"
                dot={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-full flex items-center justify-center text-sm text-slate-400">
          {emptyLabel}
        </div>
      )}
    </div>
  );
}

function FooterStat({ label, value, icon }: { label: string; value: string; icon: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
      <span className="text-slate-400">{icon}</span>
      {label}
      <span className="font-semibold text-slate-700 tabular-nums">{value}</span>
    </span>
  );
}

const ICON_CLOCK = (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ICON_BOLT = (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const ICON_SNOW = (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 3v18M4.5 7.5l15 9M19.5 7.5l-15 9" />
  </svg>
);

const ICON_QUEUE = (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 6h16M4 12h16M4 18h10" />
  </svg>
);

/**
 * Time from the request being accepted to it actually starting: queue wait plus,
 * for the first request on a fresh instance, the container boot it paid for.
 */
export function StartupBreakdownPanel({
  data,
  points,
  loading,
}: {
  data: ApiStatsResponse | null;
  points: LatencyPoint[];
  loading: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const totals = data?.totals;
  // Startup is only known for calls recorded since the timing instrumentation
  // landed, so an all-null window means "nothing measured", not "instant".
  const measured = (totals?.startup_p90 ?? null) !== null || (totals?.cold_boots ?? 0) > 0;

  return (
    <PanelCard
      title="Startup breakdown"
      subtitle="Time from gateway receipt to execution start (queue + boot)."
      icon={ICON_CLOCK}
      right={
        <div className="text-right shrink-0">
          <span className="text-lg font-bold text-slate-700 tabular-nums">
            {formatSeconds(totals?.startup_p90 ?? null)}
          </span>
          <span className="ml-1 text-[10px] text-slate-500 uppercase">p90</span>
        </div>
      }
      footer={
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-4 flex-wrap">
            <FooterStat
              label="Queue p90"
              value={formatSeconds(totals?.queue_p90 ?? null)}
              icon={ICON_QUEUE}
            />
            <FooterStat
              label="Cold boot p90"
              value={formatSeconds(totals?.boot_p90 ?? null)}
              icon={ICON_SNOW}
            />
            <FooterStat
              label="Cold boot rate"
              value={`${((totals?.cold_boot_rate ?? 0) * 100).toFixed(0)}%`}
              icon={ICON_BOLT}
            />
          </div>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-indigo-600"
          >
            {expanded ? 'Hide breakdown' : 'Show breakdown'}
            <svg
              className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      }
    >
      <LatencyChart
        points={points}
        loading={loading}
        hasData={measured}
        showBreakdown={expanded}
        emptyLabel="No startup timings in this period"
      />
    </PanelCard>
  );
}

/** How long a request takes once it has actually started running. */
export function ExecutionDurationPanel({
  data,
  points,
  loading,
}: {
  data: ApiStatsResponse | null;
  points: LatencyPoint[];
  loading: boolean;
}) {
  const totals = data?.totals;
  return (
    <PanelCard
      title="Execution duration"
      subtitle="The time it takes for a request to run once it's started."
      icon={ICON_BOLT}
      right={
        <div className="text-right shrink-0">
          <span className="text-lg font-bold text-slate-700 tabular-nums">
            {formatSeconds(totals?.p90 ?? null)}
          </span>
          <span className="ml-1 text-[10px] text-slate-500 uppercase">p90</span>
        </div>
      }
    >
      <LatencyChart
        points={points}
        loading={loading}
        hasData={(totals?.total ?? 0) > 0}
        emptyLabel="No requests in this period"
      />
    </PanelCard>
  );
}

/**
 * ID / When / Total time / Status table, used for both the recent-errors and
 * slowest-requests panels. "Total time" is the end-to-end wall clock where it
 * was recorded, falling back to execution time for older rows.
 */
export function CallSliceTable({
  title,
  rows,
  loading,
  emptyLabel,
  emptyTone,
  slugFor,
  labelFor,
  showModel = true,
  right,
}: {
  title: string;
  rows: ApiStatsCallRow[];
  loading: boolean;
  emptyLabel: string;
  emptyTone?: 'success';
  /** Route slug for an endpoint, so a row can link to its request history. */
  slugFor?: (modelType: string) => string | null;
  labelFor?: (modelType: string) => string;
  showModel?: boolean;
  right?: ReactNode;
}) {
  // Written out in full (not interpolated) so Tailwind can see the classes.
  const cols = showModel
    ? 'sm:grid-cols-[minmax(0,1fr)_minmax(0,8rem)_5.5rem_4rem]'
    : 'sm:grid-cols-[minmax(0,1fr)_5.5rem_4rem]';

  return (
    <div className="rounded-xl border border-slate-200 shadow-soft p-4 bg-white flex flex-col">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        {right}
      </div>

      {loading && rows.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-sm text-slate-500 py-8">
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div
          className={`flex-1 flex flex-col items-center justify-center py-8 text-sm ${
            emptyTone === 'success' ? 'text-success-700' : 'text-slate-400'
          }`}
        >
          <span className="font-medium">No requests found</span>
          <span className="text-xs text-slate-400 mt-0.5">{emptyLabel}</span>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          <div
            className={`hidden sm:grid ${cols} gap-2 pb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400`}
          >
            <span>ID</span>
            {showModel && <span>Model</span>}
            <span className="text-right">Total time (s)</span>
            <span className="text-right">Status</span>
          </div>
          {rows.map((row) => {
            const slug = slugFor && row.model_type ? slugFor(row.model_type) : null;
            const seconds = row.total_seconds ?? row.duration_seconds;
            return (
              <div
                key={row.id}
                className={`grid grid-cols-2 ${cols} gap-2 py-2 text-sm items-center`}
              >
                <span className="min-w-0 col-span-2 sm:col-span-1">
                  <span className="flex items-center gap-1.5">
                    <code className="text-xs font-mono text-slate-600 truncate" title={row.id}>
                      {row.id.slice(0, 8)}
                    </code>
                    <InlineCopyButton value={row.id} title="Copy request ID" />
                  </span>
                  <span className="block text-[10px] text-slate-400">
                    {row.created_at
                      ? formatDistanceToNow(new Date(row.created_at), { addSuffix: true })
                      : '—'}
                  </span>
                  {row.error && (
                    <span className="block text-[11px] text-danger line-clamp-2 break-words mt-0.5">
                      {row.error}
                    </span>
                  )}
                </span>
                {showModel && (
                  <span className="text-xs text-slate-600 truncate" data-label="Model">
                    {slug ? (
                      <Link
                        to={`/gen-api/${slug}?tab=requests`}
                        className="hover:text-indigo-600 hover:underline"
                      >
                        {labelFor && row.model_type ? labelFor(row.model_type) : row.model_type}
                      </Link>
                    ) : (
                      (labelFor && row.model_type ? labelFor(row.model_type) : row.model_type) || '—'
                    )}
                  </span>
                )}
                <span
                  className="text-right text-xs font-mono text-slate-700 tabular-nums"
                  data-label="Total time"
                  title={
                    row.total_seconds != null
                      ? 'End-to-end: accepted to finished'
                      : 'Execution only (end-to-end not recorded for this call)'
                  }
                >
                  <span className="sm:hidden text-[10px] uppercase text-slate-400 mr-1">Time</span>
                  {seconds != null ? seconds.toFixed(4) : '—'}
                </span>
                <span className="text-right" data-label="Status">
                  <RowStatus status={row.status} />
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RowStatus({ status }: { status: string | null }) {
  const ok = status === 'success';
  const pending = status === 'pending' || status === 'processing';
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-semibold font-mono ${
        ok
          ? 'bg-success-100 text-success-700'
          : pending
            ? 'bg-slate-100 text-slate-500'
            : 'bg-danger-100 text-danger-700'
      }`}
      title={ok ? 'HTTP 200 · success' : pending ? `Still ${status}` : 'Provider error'}
    >
      {ok ? '200' : pending ? status : 'error'}
    </span>
  );
}
